import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAcumenCommand } from '../../context/AcumenCommandContext';

const AcumenCursorOverlay = () => {
  const { activeTool } = useAcumenCommand();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const mouseRef = useRef({ x: 0, y: 0 });
  const overlayRef = useRef(null);
  const outlineElementsRef = useRef(new Set());

  // Tool configuration
  const toolConfig = {
    vibe: { color: '#3b82f6', glyph: 'B' },
    context: { color: '#10b981', glyph: '+' },
    help: { color: '#f59e0b', glyph: '?' },
    comply: { color: '#ef4444', glyph: '✓' },
    generate: { color: '#8b5cf6', glyph: 'P' }
  };

  useEffect(() => {
    if (!activeTool) {
      // Restore default cursor
      document.body.style.cursor = 'auto';
      return;
    }

    // Hide default cursor
    document.body.style.cursor = 'none';

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseOver = (e) => {
      const target = e.target;
      
      // Check if element is interactive
      const interactiveElements = [
        'BUTTON', 'INPUT', 'TEXTAREA', 'P', 
        'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
        ...Array.from(document.querySelectorAll('div[data-section]'))
      ];
      
      if (interactiveElements.some(el => el === target || el.contains(target))) {
        // Add outline
        target.style.outline = `2px solid ${toolConfig[activeTool].color}`;
        target.style.outlineOffset = '2px';
        outlineElementsRef.current.add(target);
      }
    };

    const handleMouseOut = (e) => {
      const target = e.target;
      
      if (outlineElementsRef.current.has(target)) {
        // Remove outline
        target.style.outline = '';
        target.style.outlineOffset = '';
        outlineElementsRef.current.delete(target);
      }
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    // Cleanup function
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      
      // Restore cursor on cleanup
      document.body.style.cursor = 'auto';
      
      // Remove any remaining outlines
      outlineElementsRef.current.forEach(el => {
        el.style.outline = '';
        el.style.outlineOffset = '';
      });
    };
  }, [activeTool]);

  if (!activeTool) return null;

  const toolColor = toolConfig[activeTool].color;
  const glyph = toolConfig[activeTool].glyph;

  // Create portal content
  const cursorDot = (
    <div
      ref={overlayRef}
      style={{
        position: 'fixed',
        left: mousePosition.x - 10,
        top: mousePosition.y - 10,
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: toolColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '12px',
        fontWeight: 'bold',
        pointerEvents: 'none',
        zIndex: 999999,
        boxShadow: `0 0 8px ${toolColor}, 0 0 16px ${toolColor}`,
        transform: 'translate(-50%, -50%)'
      }}
    >
      {glyph}
    </div>
  );

  return ReactDOM.createPortal(cursorDot, document.body);
};

export default AcumenCursorOverlay;
